




























































































































































































































































import { mapActions, mapGetters } from 'vuex';

import { ALForm } from '@/common/component.types';

import {
  diffDeep,
  getPropertyFromItem,
  setErrorFromAxiosError
} from '@/utils/helpers';

import { Group } from '@vue-altoleap-libraries/vue-altoleap-accounts-lib';
import {
  IWorkflowState,
  IWorkflowStateData,
  IWorkflowStateReminderConfigData,
  WorkflowState,
  WorkflowStateAssignmentTypeEnum
} from '@/models';
import { SystemError } from '@/models/error/error';
import { ISystemError } from '@/models/error/error.types';

import mixins from '@/utils/mixins';
import authMixin from '@/mixins/auth';
import { requiredValidator } from '@/utils/validators';

import ALDialog from '@/components/core/Dialog/ALDialog.vue';
import ErrorAlert from '@/components/common/ErrorAlert.vue';
import GroupAutoCompletePicker from '@/components/account/GroupAutoCompletePicker.vue';
import WorkflowStateThresholdsGroupList from './WorkflowStateThresholdsGroupList.vue';
import WorkflowStateReminderConfigFormFields from './WorkflowStateReminderConfigFormFields.vue';

const baseMixins = mixins(authMixin);

export enum WizardSteps {
  Form,
  Reminder,
  Threshold
}

export interface Options extends InstanceType<typeof baseMixins> {
  $refs: {
    form: ALForm;
    reminderForm: ALForm;
    groupAutoCompletePickerRef: InstanceType<typeof GroupAutoCompletePicker>;
  };
}

export default baseMixins
  .extend<Options>({
    functional: false
  })
  .extend({
    name: 'WorkflowStateFormDialog',

    components: {
      ALDialog,
      ErrorAlert,
      GroupAutoCompletePicker,
      WorkflowStateThresholdsGroupList,
      WorkflowStateReminderConfigFormFields
    },

    props: {
      workflowId: { type: Number, required: true }
    },

    data() {
      return {
        dialog: false,
        isLoading: false,
        isEdit: false,
        isFormValid: false,
        isReminderFormValid: false,

        workflowStateData: {
          groups: [],
          config: {}
        } as IWorkflowStateData,

        reminderConfigData: {
          interval_in_days: 1,
          email: {
            subject: '',
            body: ''
          }
        } as IWorkflowStateReminderConfigData,

        tags: [] as number[],
        error: null as ISystemError | null,

        WorkflowStateAssignmentTypeEnum,

        step: WizardSteps.Form,
        WizardSteps
      };
    },

    computed: {
      ...mapGetters({
        getWorkflowById: 'workflow/getWorkflowById',
        getWorkflowStateTypeVisuals: 'workflow/getWorkflowStateTypeVisuals',
        getWorkflowStateAssignmentTypeVisuals:
          'workflow/getWorkflowStateAssignmentTypeVisuals',
        lastUpsertedWorkflowStateId: 'workflow/getLastUpsertedWorkflowStateId',
        getWorkflowStateById: 'workflow/getWorkflowStateById'
      }),

      title(): string {
        switch (this.step) {
          case WizardSteps.Form:
            return this.isEdit
              ? 'Edit Workflow State'
              : 'Create Workflow State';

          case WizardSteps.Reminder:
            return 'Workflow State Reminder';

          case WizardSteps.Threshold:
          default:
            return 'Workflow State Thresholds';
        }
      },

      canUpsertThresholds(): boolean {
        return (
          this.userHasPermissions([
            'workflows.add_statethreshold',
            'workflows.change_statethreshold'
          ]) &&
          this.workflowStateData.assignment_type ===
            WorkflowStateAssignmentTypeEnum.OrgChart
        );
      },

      groupsProvider(): any {
        return this.$altoleapAccounts.groupsProvider;
      },

      computedGroups(): Group[] {
        return this.workflowStateData.groups!.map(group => {
          return new Group({
            id: getPropertyFromItem(group as Group, 'id', group),
            name: this.groupsProvider.getGroupById(
              getPropertyFromItem(group as Group, 'id', group)
            )?.name
          });
        });
      }
    },

    methods: {
      requiredValidator,

      ...mapActions({
        createWorkflowState: 'workflow/createWorkflowState',
        updateWorkflowState: 'workflow/updateWorkflowState'
      }),

      resetFormValidation(): void {
        if (this.$refs.form) {
          return this.$refs.form.resetValidation();
        }
      },

      validateForm(): boolean {
        let valid = false;
        if (this.$refs.form) {
          valid = this.$refs.form.validate();
        }
        return valid;
      },

      validateReminderForm(): boolean {
        let valid = false;
        if (this.$refs.form) {
          valid = this.$refs.reminderForm.validate();
        }
        return valid;
      },

      open(state?: IWorkflowState) {
        this.clearErrors();
        this.resetFormValidation();
        this.isEdit = !!state;
        this.step = WizardSteps.Form;

        this.workflowStateData = this.isEdit
          ? new WorkflowState(state!)
          : ({
              groups: [],
              config: {
                color: this.$vuetify.theme.currentTheme.primary,
                assignment_type: WorkflowStateAssignmentTypeEnum.None
              }
            } as IWorkflowStateData);

        if (state?.reminder_config) {
          this.reminderConfigData = state.reminder_config;
        }

        this.dialog = true;
      },

      close() {
        this.dialog = false;
      },

      clearErrors() {
        this.error = null;
      },

      updateGroup(group: number) {
        this.clearErrors();
        // group may be a number or an object depending if the value is coming from the server or locally
        const groupIndex = this.workflowStateData.groups!.findIndex(
          permissionGroup =>
            group ===
            getPropertyFromItem(permissionGroup as Group, 'id', permissionGroup)
        );

        // if tag not currently in tag list as to not add duplicates
        if (groupIndex === -1) {
          this.workflowStateData.groups!.push(group);
        }

        this.$nextTick(() => {
          this.$refs.groupAutoCompletePickerRef.reset();
        });
      },

      removeGroup(groupId: number) {
        // group may be a number or an object depending if the value is coming from the server or locally
        const groupIndex = this.workflowStateData.groups!.findIndex(
          permissionGroup =>
            groupId ===
            getPropertyFromItem(permissionGroup as Group, 'id', permissionGroup)
        );

        this.workflowStateData.groups!.splice(groupIndex, 1);
      },

      moveToThresholdForm() {
        if (this.validateForm()) {
          this.step = WizardSteps.Threshold;
        }
      },

      moveToReminderForm() {
        if (this.validateForm()) {
          this.step = WizardSteps.Reminder;
        }
      },

      saveReminderConfig() {
        this.clearErrors();
        const valid = this.validateReminderForm();
        if (valid) {
          this.workflowStateData.reminder_config = this.reminderConfigData;
          this.save();
        }
      },

      save() {
        this.clearErrors();
        const valid = this.validateForm();

        if (valid) {
          let workflowStateInstance: IWorkflowState | null = null;

          try {
            workflowStateInstance = new WorkflowState(this.workflowStateData);
          } catch (error) {
            if (SystemError.isSystemError(error)) {
              this.error = error;
            }
          }

          if (workflowStateInstance != null) {
            this.isLoading = true;

            const payloadDelta = this.isEdit
              ? diffDeep(
                  workflowStateInstance,
                  this.getWorkflowStateById(workflowStateInstance.id),
                  true
                )
              : workflowStateInstance;
            const saveAction = this.isEdit
              ? this.updateWorkflowState
              : this.createWorkflowState;

            return saveAction({
              workflowId: this.workflowId,
              state: payloadDelta
            })
              .then(() => {
                this.$emit(
                  this.isEdit ? 'updated-state' : 'created-state',
                  this.getWorkflowStateById(this.lastUpsertedWorkflowStateId)
                );

                // If enabling the reminder and user is on the first step of the form then go to the reminder step else go to the threshold step if we can move to threshold
                if (
                  (workflowStateInstance!.enable_reminder &&
                    this.step === WizardSteps.Form) ||
                  this.canUpsertThresholds
                ) {
                  this.workflowStateData = this.getWorkflowStateById(
                    this.lastUpsertedWorkflowStateId
                  );
                  this.isEdit = true;
                  if (
                    workflowStateInstance!.enable_reminder &&
                    this.step === WizardSteps.Form
                  ) {
                    this.moveToReminderForm();
                  } else {
                    this.moveToThresholdForm();
                  }
                } else {
                  this.close();
                }
              })
              .catch(error => {
                this.error = setErrorFromAxiosError(error);
              })
              .finally(() => {
                this.isLoading = false;
              });
          }
        }
      }
    }
  });
